/**
 * 005-packing-verification, admin-packing-oversight bucket. T053's `GET /oversight/tasks` columns,
 * plus T059's per-row assign/unassign — wired here because the action needs the row's own task,
 * not because it's presentational. Packers are picked from Identity's own account list
 * (`GET /api/identity/users?status=active&permission=packing.capture` — the accounts the live matrix
 * lets pack), and an assigned packer is shown by name.
 * The assigned packer's name is the API's own `assignedToName`. When the list is unreadable (the
 * caller may not hold the users permission) the cell says so — there is no type-an-id fallback.
 */
import { createContext, useContext, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Badge, Button, Flex, Select, Text } from "rizzui";
import { getStatusBadge } from "@steelyard/theme";
import { formatMoment } from "../../lib/format";
import { assignPackingTask, personName, unassignPackingTask, type PackingTaskRow } from "../../lib/services/warehouse";
import { orderPath } from "../../lib/paths";

/**
 * Active staff accounts, id → name and email; `null` when Identity's user list could not be read,
 * `undefined` while it is still loading. Read through context, not the column closure, because
 * `PagedTable` freezes its columns at mount and the list arrives after the tasks do.
 */
export type PackerDirectory = Map<string, { name: string; email?: string }> | null | undefined;
export const PackersContext = createContext<PackerDirectory>(null);

function AssignCell({ task, onChanged }: { task: PackingTaskRow; onChanged: () => void }) {
  const packers = useContext(PackersContext);
  const [packerId, setPackerId] = useState("");
  const [pending, setPending] = useState(false);

  async function assign() {
    if (!packerId) return;
    setPending(true);
    try {
      const assigned = await assignPackingTask(task.id, packerId);
      toast.success(`Assigned to ${personName(assigned.assignedToName)}`);
      setPackerId("");
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not assign this task");
    } finally {
      setPending(false);
    }
  }

  async function unassign() {
    setPending(true);
    try {
      await unassignPackingTask(task.id);
      toast.success("Returned to the queue");
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not unassign this task");
    } finally {
      setPending(false);
    }
  }

  // A finished task has nothing left to hand out: it shows who packed it, and no control.
  const finished = task.status === "complete" || task.status === "cancelled";
  if (finished && !task.assignedTo) return <Text className="text-gray-500">—</Text>;

  // One line per row: who has it (email on hover), and the one action that applies.
  if (task.assignedTo) {
    return (
      <Flex align="center" justify="between" gap="3">
        <span className="truncate font-medium text-gray-900" title={packers?.get(task.assignedTo)?.email}>
          {personName(task.assignedToName)}
        </span>
        {finished ? null : (
          <Button size="sm" variant="outline" disabled={pending} onClick={unassign} className="shrink-0">
            {pending ? "Unassigning…" : "Unassign"}
          </Button>
        )}
      </Flex>
    );
  }

  if (packers === null) {
    return <Text className="text-xs text-gray-500">The list of packers could not be loaded, so this task cannot be assigned here.</Text>;
  }

  return (
    <Flex align="center" gap="2" className="w-full">
      <Select
          aria-label={`Packer for ${task.orderNumber}`}
          placeholder={packers ? "Choose who packs" : "Loading packers…"}
          disabled={!packers}
          // The email disambiguates two people with the same name; the closed control shows the name.
          options={[...(packers ?? [])].map(([value, p]) => ({ value, label: p.email ? `${p.name} · ${p.email}` : p.name }))}
          value={packers?.has(packerId) ? { value: packerId, label: packers.get(packerId)!.name } : null}
          onChange={(option) => setPackerId((option as { value: string }).value)}
          inPortal
          className="min-w-0 flex-1"
        />
      <Button size="sm" disabled={pending || !packerId} onClick={assign} className="shrink-0">
        {pending ? "Assigning…" : "Assign"}
      </Button>
    </Flex>
  );
}

/**
 * `accessorKey`/`accessorFn` on the data columns are what the search box matches and what a header
 * click sorts — TanStack does neither for a `cell`-only column. The whole task list arrives in one
 * response, so a sort here covers every task. The two review flags sit under the order number
 * rather than in a column that is empty on almost every row.
 */
export function taskColumns(onChanged: () => void): ColumnDef<PackingTaskRow>[] {
  return [
    {
      id: "order",
      accessorKey: "orderNumber",
      header: "Order",
      size: 260,
      cell: ({ row }) => (
        <Flex direction="col" align="start" gap="1">
          <Link to={orderPath(row.original.orderNumber)} className="font-semibold text-gray-900 hover:underline">
            {row.original.orderNumber}
          </Link>
          <Text className="text-xs uppercase text-gray-500">{row.original.customerType}</Text>
          {row.original.flaggedForReview || row.original.hasUnresolvedConflict ? (
            <Flex gap="1" className="flex-wrap">
              {row.original.flaggedForReview ? <Badge size="sm" variant="flat" color="danger">Flagged for review</Badge> : null}
              {row.original.hasUnresolvedConflict ? <Badge size="sm" variant="flat" color="danger">Unresolved conflict</Badge> : null}
            </Flex>
          ) : null}
        </Flex>
      ),
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      size: 160,
      cell: ({ row }) => getStatusBadge(row.original.status),
    },
    {
      id: "lines",
      // The API's own line array's length — a row count, nothing derived.
      accessorFn: (task) => task.lines?.length,
      sortUndefined: "last",
      header: "Lines",
      size: 130,
      meta: { align: "right" },
      cell: ({ row }) => (
        <Flex direction="col" align="end" gap="0">
          <span className="font-semibold tabular-nums">{row.original.lines?.length ?? "—"}</span>
          {row.original.everyLinePacked ? <Text className="text-xs text-gray-500">all packed</Text> : null}
        </Flex>
      ),
    },
    {
      id: "createdAt",
      accessorKey: "createdAt",
      header: "Created",
      size: 180,
      cell: ({ row }) => <Text className="text-gray-600">{formatMoment(row.original.createdAt)}</Text>,
    },
    {
      id: "actions",
      accessorFn: (row) => (row.assignedTo ? personName(row.assignedToName) : ""),
      enableSorting: false,
      header: "Assignment",
      size: 300,
      cell: ({ row }) => <AssignCell task={row.original} onChanged={onChanged} />,
    },
  ];
}
